<script setup lang="ts">
import { EngagementMode } from '@/core/sourcing/outreach/outreach.persistence';

defineProps<{
  modelValue: EngagementMode;
  isContactModeEnabled: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: EngagementMode];
}>();
</script>

<template>
  <div class="flex flex-col gap-3">
    <h3 class="text-gray-900 m-0 font-sans text-base font-bold leading-6">
      How should your AI recruiter handle these candidates?
    </h3>

    <div class="flex flex-col gap-3">
      <!-- Screen as Applicants -->
      <div
        class="cursor-pointer rounded-xl border bg-white p-4 transition-all"
        :class="
          modelValue === EngagementMode.SCREEN
            ? 'border border-highlight-500 p-[15px]'
            : 'border border-tint-80 hover:bg-tint-20'
        "
        @click="emit('update:modelValue', EngagementMode.SCREEN)"
      >
        <div class="flex flex-col gap-1.5">
          <h4
            class="m-0 font-sans text-sm leading-[21px]"
            :class="
              modelValue === EngagementMode.SCREEN
                ? 'font-extrabold text-black'
                : 'font-bold text-shade-800'
            "
          >
            Screen as applicants
          </h4>
          <p class="m-0 font-sans text-sm font-normal leading-[14px] text-shade-800">
            Add candidates to the 'New' stage for immediate screening
          </p>
        </div>
      </div>

      <!-- Contact as Prospects -->
      <div
        class="rounded-xl border bg-white p-4 transition-all"
        :class="[
          isContactModeEnabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-50',
          modelValue === EngagementMode.CONTACT
            ? 'border border-highlight-500 p-[15px]'
            : 'border border-tint-80',
          isContactModeEnabled && modelValue !== EngagementMode.CONTACT ? 'hover:bg-tint-20' : '',
        ]"
        @click="isContactModeEnabled && emit('update:modelValue', EngagementMode.CONTACT)"
      >
        <div class="flex flex-col gap-1.5">
          <h4
            class="m-0 font-sans text-sm leading-[21px]"
            :class="
              modelValue === EngagementMode.CONTACT
                ? 'font-extrabold text-black'
                : 'font-bold text-shade-800'
            "
          >
            Contact as prospects
          </h4>
          <p class="m-0 font-sans text-sm font-normal leading-[14px] text-shade-800">
            Add candidates to the 'Sourcing' stage for AI-driven outreach
          </p>
          <p
            v-if="!isContactModeEnabled"
            class="m-0 font-sans text-xs font-normal italic leading-[14px] text-shade-600"
          >
            Let your AI recruiter source passive candidates for this job to use this option
          </p>
        </div>
      </div>

      <!-- Skip engagement entirely -->
      <div
        class="cursor-pointer rounded-xl border bg-white p-4 transition-all"
        :class="
          modelValue === EngagementMode.SKIP
            ? 'border border-highlight-500 p-[15px]'
            : 'border border-tint-80 hover:bg-tint-20'
        "
        @click="emit('update:modelValue', EngagementMode.SKIP)"
      >
        <div class="flex flex-col gap-1.5">
          <h4
            class="m-0 font-sans text-sm leading-[21px]"
            :class="
              modelValue === EngagementMode.SKIP
                ? 'font-extrabold text-black'
                : 'font-bold text-shade-800'
            "
          >
            Skip engagement entirely
          </h4>
          <p class="m-0 font-sans text-sm font-normal leading-[14px] text-shade-800">
            Add candidates to the 'New' stage with no automated messaging
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
